import * as THREE from "three";
import type { Tile, Vec2 } from "../core/types";

/**
 * Grid-to-world mapping.
 *
 * One maze tile is `TILE` world units square. Two units was chosen so the
 * exported models sit at roughly their native scale: the astronaut is 1.88
 * units tall, which reads correctly in a two-unit-wide corridor.
 *
 * Grid `y` runs south, world `z` runs the same way, so the mapping is direct
 * and nothing in the renderer has to flip a sign.
 */
export const TILE = 2;
// Deliberately low. At full corridor height the hull hides the astronaut and
// the Story Points behind it from an isometric camera; knee-high bulkheads keep
// the whole deck legible.
export const WALL_HEIGHT = 0.8;
export const FLOOR_Y = 0;

/**
 * The isometric camera bearing.
 *
 * `atan(1/sqrt(2))` is the pitch at which a cube shows all three faces equally,
 * which is what makes the projection read as true isometric.
 */
export const ISO_PITCH = Math.atan(1 / Math.SQRT2);
export const ISO_YAW = Math.PI / 4;

/**
 * The `rotation.y` that turns a character to face the camera.
 *
 * The camera sits at `+X, +Z` from whatever it looks at, and a model's forward
 * is local `+X`, which a Y-rotation of `t` sends to world `(cos t, 0, -sin t)`.
 * Solving that against the camera's `(cos ISO_YAW, sin ISO_YAW)` bearing gives
 * `-ISO_YAW`. Derived rather than hardcoded so it cannot drift if the camera
 * angle is ever retuned.
 */
export const CAMERA_FACING_Y = -ISO_YAW;

export const toWorld = (pos: Vec2 | Tile, y = FLOOR_Y): THREE.Vector3 =>
  new THREE.Vector3(pos.x * TILE, y, pos.y * TILE);

export const worldWidth = (tiles: number): number => tiles * TILE;
